import { store } from '../../app/store';
import { appDebugLog } from '../../Util/appDebugLog';
import { describeJwtToken, isJwtExpired } from '../../Util/jwtUtils';
import { refreshAccessToken } from '../instances/VoiceIQAPI';
import BeaconService from './BeaconService';

const GATEWAY_REFRESH_SKEW_MS = 20_000;

export type BeaconConnectionCredentials = {
  connectionUrl: string;
  accessToken: string;
};

export type ResolveBeaconConnectionOptions = {
  /**
   * Force POST /connect/refresh even if gateway JWT still valid.
   * Use when WebSocket got 401 or reconnect after long idle.
   */
  forceGatewayRefresh?: boolean;
  /**
   * Gateway Bearer was just rotated (e.g. proactive refresh). Skip a second refresh;
   * only mint a new Beacon WebSocket JWT via `/api/v1/beacon/connect`.
   */
  gatewayAlreadyRefreshed?: boolean;
};

/**
 * Obtain a fresh Beacon WebSocket URL + JWT via `/api/v1/beacon/connect`.
 *
 * Two tokens are involved:
 * - **Gateway Bearer** (`authUser.AccessToken`): authorizes REST calls (incl. POST /api/v1/beacon/connect).
 * - **Beacon WebSocket JWT** (`accessToken` from connect response): query param on GET /connect only.
 *
 * Refreshing the gateway token does not update the WebSocket JWT; we must call connect again.
 */
export async function resolveBeaconConnection(
  options: ResolveBeaconConnectionOptions | boolean = false
): Promise<BeaconConnectionCredentials> {
  const opts: ResolveBeaconConnectionOptions =
    typeof options === 'boolean' ? { forceGatewayRefresh: options } : options ?? {};
  const forceGatewayRefresh = opts.forceGatewayRefresh ?? false;
  const gatewayAlreadyRefreshed = opts.gatewayAlreadyRefreshed ?? false;
  const auth = store.getState()?.auth?.authUser;
  if (!auth?.tenantCode) {
    throw new Error('Missing tenant for beacon connect');
  }

  let gatewayAccessToken = auth.AccessToken;
  if (!gatewayAccessToken) {
    throw new Error('Missing access token for beacon connect');
  }

  const gatewayNeedsRefresh =
    !gatewayAlreadyRefreshed &&
    (forceGatewayRefresh || isJwtExpired(gatewayAccessToken, GATEWAY_REFRESH_SKEW_MS));

  if (gatewayNeedsRefresh) {
    appDebugLog('[WebSocketAuth] refreshAccessToken', {
      forceGatewayRefresh,
      gateway: describeJwtToken(gatewayAccessToken, 'gatewayJwt'),
    });
    gatewayAccessToken = await refreshAccessToken();
    appDebugLog('[WebSocketAuth] Gateway Bearer refreshed', describeJwtToken(gatewayAccessToken, 'gatewayJwt'));
  } else if (gatewayAlreadyRefreshed) {
    appDebugLog('[WebSocketAuth] Gateway already refreshed — minting Beacon WebSocket JWT only');
  }

  appDebugLog('[WebSocketAuth] POST /api/v1/beacon/connect');

  const tenantSettings = store.getState()?.settings?.tenantSettings;
  const statusId = store.getState()?.auth?.user?.userStatus?.id;
  const currentUserStatus = tenantSettings?.userStatuses?.filter(
    (status: { id?: string; isAvailable?: boolean }) => status?.id === statusId
  );
  const available = currentUserStatus?.[0]?.isAvailable ?? true;

  const result = await BeaconService.getBeacon(auth.tenantCode, gatewayAccessToken, { available });
  const data = result?.data?.data ?? result?.data;

  if (!data?.connectionUrl || !data?.accessToken) {
    throw new Error('Beacon connect returned invalid payload');
  }

  if (isJwtExpired(data.accessToken, 0)) {
    throw new Error('Beacon connect returned an already-expired WebSocket token');
  }

  appDebugLog('[WebSocketAuth] New Beacon WebSocket JWT', describeJwtToken(data.accessToken, 'beaconWsJwt'));

  return {
    connectionUrl: data.connectionUrl,
    accessToken: data.accessToken,
  };
}
